import React from 'react';

interface DeleteEquipmentConfirmationModalProps {
  isOpen: boolean;
  equipmentName: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteEquipmentConfirmationModal: React.FC<DeleteEquipmentConfirmationModalProps> = ({
  isOpen,
  equipmentName,
  onConfirm,
  onCancel
}) => {
  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <>
      <style>{`
        .delete-equipment-confirm-button:hover {
          background-color: #c62828;
          transform: scale(1.02);
        }

        .delete-equipment-cancel-button:hover {
          background-color: #e0e0e0;
        }

        @keyframes modalFadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes modalSlideIn {
          from {
            transform: scale(0.9) translateY(-20px);
            opacity: 0;
          }
          to {
            transform: scale(1) translateY(0);
            opacity: 1;
          }
        }

        .modal-overlay-animated {
          animation: modalFadeIn 0.2s ease-out;
        }

        .modal-content-animated {
          animation: modalSlideIn 0.3s ease-out;
        }
      `}</style>
      <div style={styles.overlay} onClick={handleCancel} className="modal-overlay-animated">
        <div style={styles.modal} onClick={e => e.stopPropagation()} className="modal-content-animated">
          <div style={styles.modalHeader}>
            <div style={styles.iconContainer}>
              <span style={styles.trashIcon}>🗑️</span>
            </div>
            <h2 style={styles.modalTitle}>Delete Equipment?</h2>
          </div>

          <div style={styles.content}>
            <p style={styles.message}>Are you sure you want to delete this equipment?</p>
            <div style={styles.equipmentPreview}>
              <span style={styles.equipmentPreviewLabel}>Equipment:</span>
              <span style={styles.equipmentPreviewName}>{equipmentName}</span>
            </div>
            <p style={styles.warning}>This action cannot be undone.</p>
          </div>

          <div style={styles.buttonContainer}>
            <button onClick={handleCancel} style={styles.cancelButton} className="delete-equipment-cancel-button">
              Cancel
            </button>
            <button onClick={handleConfirm} style={styles.confirmButton} className="delete-equipment-confirm-button">
              Delete Equipment
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1100
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '480px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    overflow: 'hidden'
  },
  modalHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '24px 24px 16px 24px',
    borderBottom: '1px solid #f0f0f0'
  },
  iconContainer: {
    marginBottom: '12px'
  },
  trashIcon: {
    fontSize: '48px',
    display: 'block'
  },
  modalTitle: {
    margin: 0,
    fontSize: '22px',
    fontWeight: '600',
    color: '#333',
    textAlign: 'center'
  },
  content: {
    padding: '24px'
  },
  message: {
    margin: '0 0 16px 0',
    fontSize: '15px',
    color: '#666',
    textAlign: 'center',
    lineHeight: '1.5'
  },
  equipmentPreview: {
    backgroundColor: '#FFF9C4',
    border: '1px solid #FBC02D',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  equipmentPreviewLabel: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#F57F17',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  equipmentPreviewName: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
    wordBreak: 'break-word'
  },
  warning: {
    margin: 0,
    fontSize: '13px',
    color: '#d32f2f',
    textAlign: 'center',
    fontWeight: '500'
  },
  buttonContainer: {
    display: 'flex',
    gap: '12px',
    padding: '16px 24px 24px 24px'
  },
  cancelButton: {
    flex: 1,
    padding: '12px 24px',
    backgroundColor: '#f5f5f5',
    color: '#666',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  confirmButton: {
    flex: 1,
    padding: '12px 24px',
    backgroundColor: '#d32f2f',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  }
};

export default DeleteEquipmentConfirmationModal;
